import WireOutIcon from '../../assets/icons/svg/large-icons/ic-large-wire.svg';
import MailCheckIcon from '../../assets/icons/svg/large-icons/ic-large-mail-check.svg';
import InternationalWireOutIcon from '../../assets/icons/svg/large-icons/ic-large-international_wire_out.svg';
import AchOutIcon from '../../assets/icons/svg/large-icons/ic-large-bank.svg';
import { IMoveMoneyData } from '../../store/createMoneyMoveSlice';
import { getAddressDetails } from '../Supplier/helpers';
import { convertValueToUSD } from '../../helpers/currency.helper';
import { ISupplierModel } from '../Supplier/types';
import {
  getSelectedAchFee,
  isInternationalDataValid,
  isVendorAchDataValid,
  isVendorAddressValid,
  isVendorWireDataValid,
} from './utils';
import { TRANSFER_TYPES } from './steps/Review';
import StandardIcon from '../../assets/svg/Standard.svg';
import ExpressIcon from '../../assets/svg/Express.svg';
import { getFlagEmoji } from '../../services/general.service';

export enum MoveMoneyTypes {
  WIRE_OUT = 'wire-out',
  MAIL_CHECK = 'mail-check',
  INTERNATIONAL_WIRE_OUT = 'international-wire-out',
  ASK_VENDOR = 'ask-vendor',
  ACH_OUT_TRANSFER = 'ach-out-transfer',
}

export enum MoveMoneySteps {
  PRE_LOAD = 'pre-load',
  INTRO = 'intro',
  VENDOR_PICKER = 'vendor-picker',
  VENDOR_UPDATE = 'vendor-update',
  AMOUNT = 'amount',
  PURPOSE_OF_TRANSFER = 'purpose-of-transfer',
  INTERNATIONAL_WIRE_EDD = 'international-edd',
  REVIEW = 'review',
  SUCCESS = 'success',
  MFA = 'mfa',
}

export const getAnalyticsFlowName = (moveType: MoveMoneyTypes): string => {
  if (moveType === MoveMoneyTypes.WIRE_OUT) {
    return 'Wire Out';
  }
  if (moveType === MoveMoneyTypes.MAIL_CHECK) {
    return 'Mail a Check';
  }
  if (moveType === MoveMoneyTypes.ACH_OUT_TRANSFER) {
    return 'ACH Out Transfer';
  }
  if (moveType === MoveMoneyTypes.INTERNATIONAL_WIRE_OUT) {
    return 'International Wire Out';
  }
  return 'N/A';
};

export const getAnalyticsFlowNameForSuccess = (moveType: MoveMoneyTypes): string => {
  if (moveType === MoveMoneyTypes.WIRE_OUT) {
    return 'WIRE_OUT';
  }
  if (moveType === MoveMoneyTypes.MAIL_CHECK) {
    return 'MAIL_A_CHECK';
  }
  if (moveType === MoveMoneyTypes.ACH_OUT_TRANSFER) {
    return 'ACH_OUT_TRANSFER';
  }
  if (moveType === MoveMoneyTypes.INTERNATIONAL_WIRE_OUT) {
    return 'INTERNATIONAL_WIRE_OUT';
  }
  return 'N/A';
};

export const AmountLimitErrorMap = {
  balance: 'MOVE_MONEY.STEPS.AMOUNT.ERRORS.BALANCE',
  singleLimit: 'MOVE_MONEY.STEPS.AMOUNT.ERRORS.SINGLE',
  monthlyLimit: 'MOVE_MONEY.STEPS.AMOUNT.ERRORS.MONTHLY',
  dailyLimit: 'MOVE_MONEY.STEPS.AMOUNT.ERRORS.DAILY',
} as const;

export const StepsToShowProfile = [MoveMoneySteps.INTRO];

export const IntroLogoMap = {
  [MoveMoneyTypes.WIRE_OUT]: WireOutIcon,
  [MoveMoneyTypes.MAIL_CHECK]: MailCheckIcon,
  [MoveMoneyTypes.ACH_OUT_TRANSFER]: AchOutIcon,
  [MoveMoneyTypes.INTERNATIONAL_WIRE_OUT]: InternationalWireOutIcon,
};

export const IntroContentMap = {
  [MoveMoneyTypes.WIRE_OUT]: {
    main_header: 'MOVE_MONEY.STEPS.INTRO.WIRE.MAIN_HEADER',
    title: 'MOVE_MONEY.STEPS.INTRO.WIRE.TITLE',
    content: 'MOVE_MONEY.STEPS.INTRO.WIRE.CONTENT',
    fee_data: '',
    disclaimer: 'MOVE_MONEY.STEPS.INTRO.WIRE.DISCLAIMER',
  },
  [MoveMoneyTypes.MAIL_CHECK]: {
    main_header: 'MOVE_MONEY.STEPS.INTRO.MAIL.MAIN_HEADER',
    title: 'MOVE_MONEY.STEPS.INTRO.MAIL.TITLE',
    content: 'MOVE_MONEY.STEPS.INTRO.MAIL.CONTENT',
    fee_data: 'MOVE_MONEY.STEPS.INTRO.MAIL.FEE_DATA',
    disclaimer: 'MOVE_MONEY.STEPS.INTRO.MAIL.DISCLAIMER',
  },
  [MoveMoneyTypes.ACH_OUT_TRANSFER]: {
    main_header: 'MOVE_MONEY.STEPS.INTRO.ACH_OUT.MAIN_HEADER',
    title: 'MOVE_MONEY.STEPS.INTRO.ACH_OUT.TITLE',
    content: 'MOVE_MONEY.STEPS.INTRO.ACH_OUT.CONTENT',
    fee_data: 'MOVE_MONEY.STEPS.INTRO.ACH_OUT.FEE_DATA',
    disclaimer: 'MOVE_MONEY.STEPS.INTRO.ACH_OUT.DISCLAIMER',
  },
  [MoveMoneyTypes.INTERNATIONAL_WIRE_OUT]: {
    main_header: 'MOVE_MONEY.STEPS.INTRO.INTERNATIONAL_WIRE_OUT.MAIN_HEADER',
    title: 'MOVE_MONEY.STEPS.INTRO.INTERNATIONAL_WIRE_OUT.TITLE',
    content: 'MOVE_MONEY.STEPS.INTRO.INTERNATIONAL_WIRE_OUT.CONTENT',
    fee_data: '',
    disclaimer: '',
  },
};

export const vendorManagerContentMap = {
  [MoveMoneyTypes.WIRE_OUT]: {
    title: 'MOVE_MONEY.STEPS.VENDOR_MANAGER.WIRE.TITLE',
    content: 'MOVE_MONEY.STEPS.VENDOR_MANAGER.WIRE.CONTENT',
  },
  [MoveMoneyTypes.INTERNATIONAL_WIRE_OUT]: {
    title: 'MOVE_MONEY.STEPS.VENDOR_MANAGER.WIRE.TITLE',
    content: 'MOVE_MONEY.STEPS.VENDOR_MANAGER.WIRE.CONTENT',
  },
  [MoveMoneyTypes.MAIL_CHECK]: {
    title: 'MOVE_MONEY.STEPS.VENDOR_MANAGER.MAIL.TITLE',
    content: 'MOVE_MONEY.STEPS.VENDOR_MANAGER.MAIL.CONTENT',
  },
  [MoveMoneyTypes.ACH_OUT_TRANSFER]: {
    title: 'MOVE_MONEY.STEPS.VENDOR_MANAGER.ACH_OUT.TITLE',
    content: 'MOVE_MONEY.STEPS.VENDOR_MANAGER.ACH_OUT.CONTENT',
  },
} as const;

export const vendorPickerContentMap = {
  [MoveMoneyTypes.WIRE_OUT]: {
    title: 'MOVE_MONEY.STEPS.VENDOR_PICKER.WIRE.TITLE',
    content: 'MOVE_MONEY.STEPS.VENDOR_PICKER.WIRE.CONTENT',
    sub_content: 'MOVE_MONEY.STEPS.VENDOR_PICKER.WIRE.SUB_CONTENT',
    add_list_item: 'MOVE_MONEY.STEPS.VENDOR_PICKER.WIRE.ADD_LIST_ITEM',
    add_list_item_with_content: 'MOVE_MONEY.STEPS.VENDOR_PICKER.WIRE.ADD_LIST_ITEM_WITH_CONTENT',
    list_type: 'MOVE_MONEY.STEPS.VENDOR_PICKER.WIRE.LIST_TYPE',
  },
  [MoveMoneyTypes.MAIL_CHECK]: {
    title: 'MOVE_MONEY.STEPS.VENDOR_PICKER.MAIL.TITLE',
    content: 'MOVE_MONEY.STEPS.VENDOR_PICKER.MAIL.CONTENT',
    sub_content: 'MOVE_MONEY.STEPS.VENDOR_PICKER.MAIL.SUB_CONTENT',
    add_list_item: 'MOVE_MONEY.STEPS.VENDOR_PICKER.MAIL.ADD_LIST_ITEM',
    add_list_item_with_content: 'MOVE_MONEY.STEPS.VENDOR_PICKER.MAIL.ADD_LIST_ITEM_WITH_CONTENT',
    list_type: 'MOVE_MONEY.STEPS.VENDOR_PICKER.MAIL.LIST_TYPE',
  },
  [MoveMoneyTypes.ACH_OUT_TRANSFER]: {
    title: 'MOVE_MONEY.STEPS.VENDOR_PICKER.ACH_OUT.TITLE',
    content: 'MOVE_MONEY.STEPS.VENDOR_PICKER.ACH_OUT.CONTENT',
    sub_content: 'MOVE_MONEY.STEPS.VENDOR_PICKER.ACH_OUT.SUB_CONTENT',
    add_list_item: 'MOVE_MONEY.STEPS.VENDOR_PICKER.ACH_OUT.ADD_LIST_ITEM',
    add_list_item_with_content: 'MOVE_MONEY.STEPS.VENDOR_PICKER.ACH_OUT.ADD_LIST_ITEM_WITH_CONTENT',
    list_type: 'MOVE_MONEY.STEPS.VENDOR_PICKER.ACH_OUT.LIST_TYPE',
  },
  [MoveMoneyTypes.INTERNATIONAL_WIRE_OUT]: {
    title: 'MOVE_MONEY.STEPS.VENDOR_PICKER.INTERNATIONAL_WIRE_OUT.TITLE',
    content: 'MOVE_MONEY.STEPS.VENDOR_PICKER.INTERNATIONAL_WIRE_OUT.CONTENT',
    sub_content: 'MOVE_MONEY.STEPS.VENDOR_PICKER.INTERNATIONAL_WIRE_OUT.SUB_CONTENT',
    add_list_item: 'MOVE_MONEY.STEPS.VENDOR_PICKER.INTERNATIONAL_WIRE_OUT.ADD_LIST_ITEM',
    add_list_item_with_content: 'MOVE_MONEY.STEPS.VENDOR_PICKER.INTERNATIONAL_WIRE_OUT.ADD_LIST_ITEM_WITH_CONTENT',
    list_type: 'MOVE_MONEY.STEPS.VENDOR_PICKER.INTERNATIONAL_WIRE_OUT.LIST_TYPE',
  },
} as const;

export const amountContentMap = {
  [MoveMoneyTypes.WIRE_OUT]: {
    title: 'MOVE_MONEY.STEPS.AMOUNT.WIRE.TITLE',
    content: 'MOVE_MONEY.STEPS.AMOUNT.WIRE.CONTENT',
  },
  [MoveMoneyTypes.MAIL_CHECK]: {
    title: 'MOVE_MONEY.STEPS.AMOUNT.MAIL.TITLE',
    content: 'MOVE_MONEY.STEPS.AMOUNT.MAIL.CONTENT',
  },
  [MoveMoneyTypes.INTERNATIONAL_WIRE_OUT]: {
    title: 'MOVE_MONEY.STEPS.AMOUNT.INTERNATIONAL_WIRE_OUT.TITLE',
    content: 'MOVE_MONEY.STEPS.AMOUNT.INTERNATIONAL_WIRE_OUT.CONTENT',
  },
  [MoveMoneyTypes.ACH_OUT_TRANSFER]: {
    title: 'MOVE_MONEY.STEPS.AMOUNT.WIRE.TITLE',
    content: 'MOVE_MONEY.STEPS.AMOUNT.MAIL.CONTENT',
  },
} as const;

export const purposeOfTransferContentMap = {
  [MoveMoneyTypes.INTERNATIONAL_WIRE_OUT]: {
    title: 'MOVE_MONEY.STEPS.PURPOSE_OF_TRANSFER.INTERNATIONAL_WIRE_OUT.TITLE',
    options: 'MOVE_MONEY.STEPS.PURPOSE_OF_TRANSFER.INTERNATIONAL_WIRE_OUT.OPTIONS',
    other_option: 'MOVE_MONEY.STEPS.PURPOSE_OF_TRANSFER.INTERNATIONAL_WIRE_OUT.OTHER_OPTION',
    other_option_placeholder: 'MOVE_MONEY.STEPS.PURPOSE_OF_TRANSFER.INTERNATIONAL_WIRE_OUT.OTHER_OPTION_PLACEHOLDER',
  },
} as const;

export const eddContentMap = {
  [MoveMoneyTypes.INTERNATIONAL_WIRE_OUT]: {
    title: 'MOVE_MONEY.STEPS.EDD.INTERNATIONAL_WIRE_OUT.TITLE',
    content: 'MOVE_MONEY.STEPS.EDD.INTERNATIONAL_WIRE_OUT.CONTENT',
    question1: 'MOVE_MONEY.STEPS.EDD.INTERNATIONAL_WIRE_OUT.QUESTION1',
    hint1: 'MOVE_MONEY.STEPS.EDD.INTERNATIONAL_WIRE_OUT.HINT1',
    question2: 'MOVE_MONEY.STEPS.EDD.INTERNATIONAL_WIRE_OUT.QUESTION2',
    answers2: 'MOVE_MONEY.STEPS.EDD.INTERNATIONAL_WIRE_OUT.ANSWERS2',
    question3: 'MOVE_MONEY.STEPS.EDD.INTERNATIONAL_WIRE_OUT.QUESTION3',
    answers3: 'MOVE_MONEY.STEPS.EDD.INTERNATIONAL_WIRE_OUT.ANSWERS3',
  },
} as const;

export const reviewContentMap = {
  [MoveMoneyTypes.WIRE_OUT]: {
    title: 'MOVE_MONEY.STEPS.REVIEW.WIRE.TITLE',
    content: 'MOVE_MONEY.STEPS.REVIEW.WIRE.CONTENT',
    dynamic_blocks_title: 'MOVE_MONEY.STEPS.REVIEW.WIRE.DYNAMIC_BLOCKS_TITLE',
    edit_item: 'MOVE_MONEY.STEPS.REVIEW.WIRE.EDIT_ITEM',
    switch_item: 'MOVE_MONEY.STEPS.REVIEW.WIRE.SWITCH_ITEM',
    disclaimer: 'MOVE_MONEY.STEPS.REVIEW.DISCLAIMER',
    transfer_data_title: 'MOVE_MONEY.STEPS.REVIEW.WIRE.TRANSFER_DATA_TITLE',
    transfer_data_placeholder: 'MOVE_MONEY.STEPS.REVIEW.WIRE.TRANSFER_DATA_PLACEHOLDER',
    footer_disclaimer: 'MOVE_MONEY.STEPS.REVIEW.WIRE.FOOTER_DISCLAIMER',
  } as const,
  [MoveMoneyTypes.MAIL_CHECK]: {
    title: 'MOVE_MONEY.STEPS.REVIEW.MAIL.TITLE',
    content: 'MOVE_MONEY.STEPS.REVIEW.MAIL.CONTENT',
    dynamic_blocks_title: 'MOVE_MONEY.STEPS.REVIEW.MAIL.DYNAMIC_BLOCKS_TITLE',
    edit_item: 'MOVE_MONEY.STEPS.REVIEW.MAIL.EDIT_ITEM',
    switch_item: 'MOVE_MONEY.STEPS.REVIEW.MAIL.SWITCH_ITEM',
    disclaimer: 'MOVE_MONEY.STEPS.REVIEW.DISCLAIMER',
    transfer_data_title: 'MOVE_MONEY.STEPS.REVIEW.MAIL.TRANSFER_DATA_TITLE',
    transfer_data_placeholder: 'MOVE_MONEY.STEPS.REVIEW.MAIL.TRANSFER_DATA_PLACEHOLDER',
    footer_disclaimer: '',
  },
  [MoveMoneyTypes.ACH_OUT_TRANSFER]: {
    title: 'MOVE_MONEY.STEPS.REVIEW.ACH_OUT.TITLE',
    content: 'MOVE_MONEY.STEPS.REVIEW.ACH_OUT.CONTENT',
    dynamic_blocks_title: 'MOVE_MONEY.STEPS.REVIEW.ACH_OUT.DYNAMIC_BLOCKS_TITLE',
    edit_item: 'MOVE_MONEY.STEPS.REVIEW.ACH_OUT.EDIT_ITEM',
    switch_item: 'MOVE_MONEY.STEPS.REVIEW.ACH_OUT.SWITCH_ITEM',
    disclaimer: 'MOVE_MONEY.STEPS.REVIEW.DISCLAIMER',
    transfer_data_title: 'MOVE_MONEY.STEPS.REVIEW.ACH_OUT.TRANSFER_DATA_TITLE',
    transfer_data_placeholder: 'MOVE_MONEY.STEPS.REVIEW.ACH_OUT.TRANSFER_DATA_PLACEHOLDER',
    footer_disclaimer: '',
  },
  [MoveMoneyTypes.INTERNATIONAL_WIRE_OUT]: {
    title: 'MOVE_MONEY.STEPS.REVIEW.MAIL.TITLE',
    content: 'MOVE_MONEY.STEPS.REVIEW.MAIL.CONTENT',
    dynamic_blocks_title: 'MOVE_MONEY.STEPS.REVIEW.MAIL.DYNAMIC_BLOCKS_TITLE',
    edit_item: 'MOVE_MONEY.STEPS.REVIEW.MAIL.EDIT_ITEM',
    switch_item: 'MOVE_MONEY.STEPS.REVIEW.MAIL.SWITCH_ITEM',
    disclaimer: 'MOVE_MONEY.STEPS.REVIEW.DISCLAIMER',
    transfer_data_title: 'MOVE_MONEY.STEPS.REVIEW.MAIL.TRANSFER_DATA_TITLE',
    transfer_data_placeholder: 'MOVE_MONEY.STEPS.REVIEW.MAIL.TRANSFER_DATA_PLACEHOLDER',
    footer_disclaimer: '',
  },
} as const;

export const successContentMap = {
  [MoveMoneyTypes.WIRE_OUT]: {
    title: 'MOVE_MONEY.STEPS.SUCCESS.WIRE.TITLE',
    sub_title: 'MOVE_MONEY.STEPS.SUCCESS.WIRE.SUB_TITLE',
    move_time_stamp: 'MOVE_MONEY.STEPS.SUCCESS.WIRE.MOVE_TIME_STAMP',
    main_cta: 'MOVE_MONEY.STEPS.SUCCESS.WIRE.MAIN_CTA',
    secondary_cta: 'MOVE_MONEY.STEPS.SUCCESS.WIRE.SECONDARY_CTA',
    notify_vendor: 'MOVE_MONEY.STEPS.SUCCESS.WIRE.NOTIFY_VENDOR',
  },
  [MoveMoneyTypes.MAIL_CHECK]: {
    title: 'MOVE_MONEY.STEPS.SUCCESS.MAIL.TITLE',
    sub_title: 'MOVE_MONEY.STEPS.SUCCESS.MAIL.SUB_TITLE',
    move_time_stamp: 'MOVE_MONEY.STEPS.SUCCESS.MAIL.MOVE_TIME_STAMP',
    main_cta: 'MOVE_MONEY.STEPS.SUCCESS.MAIL.MAIN_CTA',
    secondary_cta: 'MOVE_MONEY.STEPS.SUCCESS.MAIL.SECONDARY_CTA',
    notify_vendor: 'MOVE_MONEY.STEPS.SUCCESS.WIRE.NOTIFY_VENDOR',
  },
  [MoveMoneyTypes.INTERNATIONAL_WIRE_OUT]: {
    title: 'MOVE_MONEY.STEPS.SUCCESS.INTERNATIONAL_WIRE.TITLE',
    sub_title: 'MOVE_MONEY.STEPS.SUCCESS.WIRE.SUB_TITLE',
    move_time_stamp: 'MOVE_MONEY.STEPS.SUCCESS.WIRE.MOVE_TIME_STAMP',
    main_cta: 'MOVE_MONEY.STEPS.SUCCESS.WIRE.MAIN_CTA',
    secondary_cta: 'MOVE_MONEY.STEPS.SUCCESS.WIRE.SECONDARY_CTA',
    notify_vendor: 'MOVE_MONEY.STEPS.SUCCESS.WIRE.NOTIFY_VENDOR',
  },
  [MoveMoneyTypes.ACH_OUT_TRANSFER]: {
    title: 'MOVE_MONEY.STEPS.SUCCESS.ACH_OUT.TITLE',
    sub_title: 'MOVE_MONEY.STEPS.SUCCESS.ACH_OUT.SUB_TITLE',
    move_time_stamp: 'MOVE_MONEY.STEPS.SUCCESS.ACH_OUT.MOVE_TIME_STAMP',
    main_cta: 'MOVE_MONEY.STEPS.SUCCESS.ACH_OUT.MAIN_CTA',
    secondary_cta: 'MOVE_MONEY.STEPS.SUCCESS.ACH_OUT.SECONDARY_CTA',
    notify_vendor: 'MOVE_MONEY.STEPS.SUCCESS.WIRE.NOTIFY_VENDOR',
  },
} as const;

export const reviewNoteMaxLengthMap = {
  [MoveMoneyTypes.WIRE_OUT]: 100,
  [MoveMoneyTypes.MAIL_CHECK]: 100,
  [MoveMoneyTypes.ACH_OUT_TRANSFER]: 100,
  [MoveMoneyTypes.INTERNATIONAL_WIRE_OUT]: 0,
};

export const isRequireDataForVendorValid = (vendor: ISupplierModel | undefined, moveType: MoveMoneyTypes): boolean => {
  if (!vendor) return false;
  switch (moveType) {
    case MoveMoneyTypes.WIRE_OUT:
      return isVendorWireDataValid(vendor) && isVendorAddressValid(vendor);
    case MoveMoneyTypes.MAIL_CHECK:
      return isVendorAddressValid(vendor);
    case MoveMoneyTypes.ACH_OUT_TRANSFER:
      return isVendorAchDataValid(vendor);
    case MoveMoneyTypes.INTERNATIONAL_WIRE_OUT:
      return isInternationalDataValid(vendor.internationalWireAccountData);
    default:
      return true;
  }
};

export const getVendorDetailsBlocks = (
  moveType: MoveMoneyTypes,
  state: IMoveMoneyData,
): { title: string; value: string; id?: string }[] => {
  switch (moveType) {
    case MoveMoneyTypes.WIRE_OUT:
      return [
        {
          title: 'MOVE_MONEY.STEPS.REVIEW.DETAILS_BLOCK.SEND_TO',
          value: state.vendorData?.name || '',
          id: 'sendToValue',
        },
        {
          title: 'MOVE_MONEY.STEPS.REVIEW.DETAILS_BLOCK.ROUTING_NUMBER',
          value: state.vendorData?.wireAccountData?.routingNumber || '',
          id: 'wireRoutingNumber',
        },
        {
          title: 'MOVE_MONEY.STEPS.REVIEW.DETAILS_BLOCK.ACCOUNT_NUMBER',
          value: state.vendorData?.wireAccountData?.accountNumber || '',
          id: 'wireAccountNumber',
        },
        {
          title: 'MOVE_MONEY.STEPS.REVIEW.DETAILS_BLOCK.ADDRESS',
          value: getAddressDetails(state?.vendorData?.supplierAddressData),
          id: 'addressValue',
        },
      ];

    case MoveMoneyTypes.ACH_OUT_TRANSFER:
      return [
        {
          title: 'MOVE_MONEY.STEPS.REVIEW.DETAILS_BLOCK.SEND_TO',
          value: state.vendorData?.name || '',
          id: 'sendToValue',
        },
        {
          title: 'MOVE_MONEY.STEPS.REVIEW.DETAILS_BLOCK.ROUTING_NUMBER',
          value: state.vendorData?.achAccountData?.routingNumber || '',
          id: 'routNumberReview',
        },
        {
          title: 'MOVE_MONEY.STEPS.REVIEW.DETAILS_BLOCK.ACCOUNT_NUMBER',
          value: state.vendorData?.achAccountData?.accountNumber || '',
          id: 'accNumberToReview',
        },
      ];

    case MoveMoneyTypes.MAIL_CHECK:
      return [
        {
          title: 'MOVE_MONEY.STEPS.REVIEW.DETAILS_BLOCK.SEND_TO',
          value: state.vendorData?.name || '',
          id: 'sendToValue',
        },
        {
          title: 'MOVE_MONEY.STEPS.REVIEW.DETAILS_BLOCK.ADDRESS',
          value: getAddressDetails(state?.vendorData?.supplierAddressData),
          id: 'addressValue',
        },
      ];

    case MoveMoneyTypes.ASK_VENDOR:
      return [
        { title: 'MOVE_MONEY.STEPS.REVIEW.ACH.SEND_TO', value: state.vendorData?.name || '' },
        {
          title: 'MOVE_MONEY.STEPS.REVIEW.ACH.ACH_ROUTING_NUMBER',
          value: 'MOVE_MONEY.STEPS.REVIEW.ACH.ACH_NUMBER_WAITING',
        },
        {
          title: 'MOVE_MONEY.STEPS.REVIEW.ACH.ACH_ACCOUNT_NUMBER',
          value: 'MOVE_MONEY.STEPS.REVIEW.ACH.ACH_NUMBER_WAITING',
        },
      ];

    case MoveMoneyTypes.INTERNATIONAL_WIRE_OUT:
      return [
        {
          title: 'MOVE_MONEY.STEPS.REVIEW.DETAILS_BLOCK.SEND_TO',
          value:
            `${getFlagEmoji(state.vendorData?.internationalWireAccountData?.internationalSupplierAddressData?.country || '')} ${state.vendorData?.name} ` ||
            '',
          id: 'sendToValue',
        },
        {
          title: 'MOVE_MONEY.STEPS.REVIEW.DETAILS_BLOCK.SWIFT',
          value: state.vendorData?.internationalWireAccountData?.swiftCode || '',
          id: 'swiftNumber',
        },
        {
          title: 'MOVE_MONEY.STEPS.REVIEW.DETAILS_BLOCK.IBAN',
          value: state.vendorData?.internationalWireAccountData?.iban || '',
          id: 'ibanNumber',
        },
      ];

    default:
      return [{ title: '', value: '' }];
  }
};

export const getSuccessDetailsBlocks = (
  moveType: MoveMoneyTypes,
  state: IMoveMoneyData,
): { title: string; value: string; id?: string }[] => {
  const feeData = getSelectedAchFee(state);
  switch (moveType) {
    case MoveMoneyTypes.WIRE_OUT:
      return [
        { title: 'MOVE_MONEY.STEPS.SUCCESS.DETAILS_BLOCK.TO', value: state.vendorData?.name || '' },
        {
          title: 'MOVE_MONEY.STEPS.SUCCESS.DETAILS_BLOCK.WIRE_ROUTING_NUMBER',
          value: state.vendorData?.wireAccountData?.routingNumber || '',
        },
        {
          title: 'MOVE_MONEY.STEPS.SUCCESS.DETAILS_BLOCK.WIRE_ACCOUNT_NUMBER',
          value: state.vendorData?.wireAccountData?.accountNumber || '',
        },
        {
          title: 'MOVE_MONEY.STEPS.SUCCESS.DETAILS_BLOCK.FEE',
          value: convertValueToUSD(state.feeData?.fees[0]?.fee || 0, 2),
        },
      ];

    case MoveMoneyTypes.ACH_OUT_TRANSFER:
      return [
        {
          title: 'MOVE_MONEY.STEPS.REVIEW.DETAILS_BLOCK.SEND_TO',
          value: state.vendorData?.name || '',
          id: 'TRANSFER_DETAILS_RECIPIENT',
        },
        {
          title: 'MOVE_MONEY.STEPS.REVIEW.DETAILS_BLOCK.ROUTING_NUMBER',
          value: state.vendorData?.achAccountData?.routingNumber || '',
          id: 'TRANSFER_DETAILS_ROUTE_NUM',
        },
        {
          title: 'MOVE_MONEY.STEPS.REVIEW.DETAILS_BLOCK.ACCOUNT_NUMBER',
          value: state.vendorData?.achAccountData?.accountNumber || '',
          id: 'TRANSFER_DETAILS_ACCOUNT_NUM',
        },
        {
          title: 'MOVE_MONEY.STEPS.SUCCESS.DETAILS_BLOCK.ESTIMATED_DELIVERY',
          value: feeData?.arrivalDateShort || '',
        },
        {
          title: 'MOVE_MONEY.STEPS.SUCCESS.DETAILS_BLOCK.TOTAL_FEES',
          value: convertValueToUSD(feeData?.fee || 0, 2),
          id: 'TRANSFER_DETAILS_FEE',
        },
      ];
    case MoveMoneyTypes.INTERNATIONAL_WIRE_OUT:
      return [
        {
          title: 'MOVE_MONEY.STEPS.REVIEW.DETAILS_BLOCK.SEND_TO',
          value:
            `${getFlagEmoji(state.vendorData?.internationalWireAccountData?.internationalSupplierAddressData?.country || '')} ${state.vendorData?.name}` ||
            '',
        },
        {
          title: 'MOVE_MONEY.STEPS.REVIEW.DETAILS_BLOCK.SWIFT',
          value: state.vendorData?.internationalWireAccountData?.swiftCode || '',
        },
        {
          title: 'MOVE_MONEY.STEPS.REVIEW.DETAILS_BLOCK.IBAN',
          value: state.vendorData?.internationalWireAccountData?.iban || '',
        },
        {
          title: 'MOVE_MONEY.STEPS.SUCCESS.DETAILS_BLOCK.FEE',
          value: convertValueToUSD(state.feeData?.fees[0]?.fee || 0, 2),
        },
      ];
    case MoveMoneyTypes.MAIL_CHECK:
      return [
        { title: 'MOVE_MONEY.STEPS.SUCCESS.DETAILS_BLOCK.SEND_TO', value: state.vendorData?.name || '' },
        {
          title: 'MOVE_MONEY.STEPS.SUCCESS.DETAILS_BLOCK.ADDRESS',
          value: getAddressDetails(state?.vendorData?.supplierAddressData),
        },
        {
          title: 'MOVE_MONEY.STEPS.SUCCESS.DETAILS_BLOCK.ESTIMATED_DELIVERY',
          value: state.feeData?.fees[0].arrivalDate || '',
        },
        {
          title: 'MOVE_MONEY.STEPS.SUCCESS.DETAILS_BLOCK.TOTAL_FEES',
          value: convertValueToUSD(state.feeData?.fees[0]?.fee || 0, 2),
        },
      ];
    default:
      return [{ title: '', value: '' }];
  }
};

export const achOutTransferContentMap = {
  [TRANSFER_TYPES.STANDARD]: {
    icon: StandardIcon,
    title: 'Standard',
  },
  [TRANSFER_TYPES.EXPRESS]: {
    icon: ExpressIcon,
    title: 'Express',
  },
};

export const CUSTOM_ERROR_TITLE_MAP = {
  4245: 'Daily Transfer Limit Reached',
};

export const countriesList = [
  { title: 'Afghanistan', value: 'AF' },
  { title: 'Albania', value: 'AL' },
  { title: 'Algeria', value: 'DZ' },
  { title: 'Andorra', value: 'AD' },
  { title: 'Angola', value: 'AO' },
  { title: 'Antigua and Barbuda', value: 'AG' },
  { title: 'Argentina', value: 'AR' },
  { title: 'Armenia', value: 'AM' },
  { title: 'Australia', value: 'AU' },
  { title: 'Austria', value: 'AT' },
  { title: 'Azerbaijan', value: 'AZ' },
  { title: 'Bahamas', value: 'BS' },
  { title: 'Bahrain', value: 'BH' },
  { title: 'Bangladesh', value: 'BD' },
  { title: 'Barbados', value: 'BB' },
  { title: 'Belarus', value: 'BY' },
  { title: 'Belgium', value: 'BE' },
  { title: 'Belize', value: 'BZ' },
  { title: 'Benin', value: 'BJ' },
  { title: 'Bhutan', value: 'BT' },
  { title: 'Bolivia', value: 'BO' },
  { title: 'Bosnia and Herzegovina', value: 'BA' },
  { title: 'Botswana', value: 'BW' },
  { title: 'Brazil', value: 'BR' },
  { title: 'Brunei', value: 'BN' },
  { title: 'Bulgaria', value: 'BG' },
  { title: 'Burkina Faso', value: 'BF' },
  { title: 'Burundi', value: 'BI' },
  { title: 'Cambodia', value: 'KH' },
  { title: 'Cameroon', value: 'CM' },
  { title: 'Canada', value: 'CA' },
  { title: 'Cape Verde', value: 'CV' },
  { title: 'Central African Republic', value: 'CF' },
  { title: 'Chad', value: 'TD' },
  { title: 'Chile', value: 'CL' },
  { title: 'China', value: 'CN' },
  { title: 'Colombia', value: 'CO' },
  { title: 'Comoros', value: 'KM' },
  { title: 'Congo', value: 'CG' },
  { title: 'Costa Rica', value: 'CR' },
  { title: 'Croatia', value: 'HR' },
  { title: 'Cuba', value: 'CU' },
  { title: 'Cyprus', value: 'CY' },
  { title: 'Czech Republic', value: 'CZ' },
  { title: 'Democratic Republic of the Congo', value: 'CD' },
  { title: 'Denmark', value: 'DK' },
  { title: 'Djibouti', value: 'DJ' },
  { title: 'Dominica', value: 'DM' },
  { title: 'Dominican Republic', value: 'DO' },
  { title: 'East Timor', value: 'TL' },
  { title: 'Ecuador', value: 'EC' },
  { title: 'Egypt', value: 'EG' },
  { title: 'El Salvador', value: 'SV' },
  { title: 'Equatorial Guinea', value: 'GQ' },
  { title: 'Eritrea', value: 'ER' },
  { title: 'Estonia', value: 'EE' },
  { title: 'Ethiopia', value: 'ET' },
  { title: 'Fiji', value: 'FJ' },
  { title: 'Finland', value: 'FI' },
  { title: 'France', value: 'FR' },
  { title: 'Gabon', value: 'GA' },
  { title: 'Gambia', value: 'GM' },
  { title: 'Georgia', value: 'GE' },
  { title: 'Germany', value: 'DE' },
  { title: 'Ghana', value: 'GH' },
  { title: 'Greece', value: 'GR' },
  { title: 'Grenada', value: 'GD' },
  { title: 'Guatemala', value: 'GT' },
  { title: 'Guinea', value: 'GN' },
  { title: 'Guinea-Bissau', value: 'GW' },
  { title: 'Guyana', value: 'GY' },
  { title: 'Haiti', value: 'HT' },
  { title: 'Honduras', value: 'HN' },
  { title: 'Hungary', value: 'HU' },
  { title: 'Iceland', value: 'IS' },
  { title: 'India', value: 'IN' },
  { title: 'Indonesia', value: 'ID' },
  { title: 'Iran', value: 'IR' },
  { title: 'Iraq', value: 'IQ' },
  { title: 'Ireland', value: 'IE' },
  { title: 'Israel', value: 'IL' },
  { title: 'Italy', value: 'IT' },
  { title: 'Ivory Coast', value: 'CI' },
  { title: 'Jamaica', value: 'JM' },
  { title: 'Japan', value: 'JP' },
  { title: 'Jordan', value: 'JO' },
  { title: 'Kazakhstan', value: 'KZ' },
  { title: 'Kenya', value: 'KE' },
  { title: 'Kiribati', value: 'KI' },
  { title: 'Kuwait', value: 'KW' },
  { title: 'Kyrgyzstan', value: 'KG' },
  { title: 'Laos', value: 'LA' },
  { title: 'Latvia', value: 'LV' },
  { title: 'Lebanon', value: 'LB' },
  { title: 'Lesotho', value: 'LS' },
  { title: 'Liberia', value: 'LR' },
  { title: 'Libya', value: 'LY' },
  { title: 'Liechtenstein', value: 'LI' },
  { title: 'Lithuania', value: 'LT' },
  { title: 'Luxembourg', value: 'LU' },
  { title: 'Madagascar', value: 'MG' },
  { title: 'Malawi', value: 'MW' },
  { title: 'Malaysia', value: 'MY' },
  { title: 'Maldives', value: 'MV' },
  { title: 'Mali', value: 'ML' },
  { title: 'Malta', value: 'MT' },
  { title: 'Marshall Islands', value: 'MH' },
  { title: 'Mauritania', value: 'MR' },
  { title: 'Mauritius', value: 'MU' },
  { title: 'Mexico', value: 'MX' },
  { title: 'Micronesia', value: 'FM' },
  { title: 'Moldova', value: 'MD' },
  { title: 'Monaco', value: 'MC' },
  { title: 'Mongolia', value: 'MN' },
  { title: 'Montenegro', value: 'ME' },
  { title: 'Morocco', value: 'MA' },
  { title: 'Mozambique', value: 'MZ' },
  { title: 'Myanmar', value: 'MM' },
  { title: 'Namibia', value: 'NA' },
  { title: 'Nauru', value: 'NR' },
  { title: 'Nepal', value: 'NP' },
  { title: 'Netherlands', value: 'NL' },
  { title: 'New Zealand', value: 'NZ' },
  { title: 'Nicaragua', value: 'NI' },
  { title: 'Niger', value: 'NE' },
  { title: 'Nigeria', value: 'NG' },
  { title: 'North Korea', value: 'KP' },
  { title: 'North Macedonia', value: 'MK' },
  { title: 'Norway', value: 'NO' },
  { title: 'Oman', value: 'OM' },
  { title: 'Pakistan', value: 'PK' },
  { title: 'Palau', value: 'PW' },
  { title: 'Panama', value: 'PA' },
  { title: 'Papua New Guinea', value: 'PG' },
  { title: 'Paraguay', value: 'PY' },
  { title: 'Peru', value: 'PE' },
  { title: 'Philippines', value: 'PH' },
  { title: 'Poland', value: 'PL' },
  { title: 'Portugal', value: 'PT' },
  { title: 'Qatar', value: 'QA' },
  { title: 'Romania', value: 'RO' },
  { title: 'Russia', value: 'RU' },
  { title: 'Rwanda', value: 'RW' },
  { title: 'Saint Kitts and Nevis', value: 'KN' },
  { title: 'Saint Lucia', value: 'LC' },
  { title: 'Saint Vincent and the Grenadines', value: 'VC' },
  { title: 'Samoa', value: 'WS' },
  { title: 'San Marino', value: 'SM' },
  { title: 'Sao Tome and Principe', value: 'ST' },
  { title: 'Saudi Arabia', value: 'SA' },
  { title: 'Senegal', value: 'SN' },
  { title: 'Serbia', value: 'RS' },
  { title: 'Seychelles', value: 'SC' },
  { title: 'Sierra Leone', value: 'SL' },
  { title: 'Singapore', value: 'SG' },
  { title: 'Slovakia', value: 'SK' },
  { title: 'Slovenia', value: 'SI' },
  { title: 'Solomon Islands', value: 'SB' },
  { title: 'Somalia', value: 'SO' },
  { title: 'South Africa', value: 'ZA' },
  { title: 'South Korea', value: 'KR' },
  { title: 'South Sudan', value: 'SS' },
  { title: 'Spain', value: 'ES' },
  { title: 'Sri Lanka', value: 'LK' },
  { title: 'Sudan', value: 'SD' },
  { title: 'Suriname', value: 'SR' },
  { title: 'Sweden', value: 'SE' },
  { title: 'Switzerland', value: 'CH' },
  { title: 'Syria', value: 'SY' },
  { title: 'Taiwan', value: 'TW' },
  { title: 'Tajikistan', value: 'TJ' },
  { title: 'Tanzania', value: 'TZ' },
  { title: 'Thailand', value: 'TH' },
  { title: 'Togo', value: 'TG' },
  { title: 'Tonga', value: 'TO' },
  { title: 'Trinidad and Tobago', value: 'TT' },
  { title: 'Tunisia', value: 'TN' },
  { title: 'Turkey', value: 'TR' },
  { title: 'Turkmenistan', value: 'TM' },
  { title: 'Tuvalu', value: 'TV' },
  { title: 'Uganda', value: 'UG' },
  { title: 'Ukraine', value: 'UA' },
  { title: 'United Arab Emirates', value: 'AE' },
  { title: 'United Kingdom', value: 'GB' },
  { title: 'United States', value: 'US' },
  { title: 'Uruguay', value: 'UY' },
  { title: 'Uzbekistan', value: 'UZ' },
  { title: 'Vanuatu', value: 'VU' },
  { title: 'Vatican City', value: 'VA' },
  { title: 'Venezuela', value: 'VE' },
  { title: 'Vietnam', value: 'VN' },
  { title: 'Yemen', value: 'YE' },
  { title: 'Zambia', value: 'ZM' },
  { title: 'Zimbabwe', value: 'ZW' },
];

export const eddCountriesList = [
  { title: 'Afghanistan', value: 'Afghanistan' },
  { title: 'Albania', value: 'Albania' },
  { title: 'Algeria', value: 'Algeria' },
  { title: 'Andorra', value: 'Andorra' },
  { title: 'Angola', value: 'Angola' },
  { title: 'Antigua and Barbuda', value: 'Antigua and Barbuda' },
  { title: 'Argentina', value: 'Argentina' },
  { title: 'Armenia', value: 'Armenia' },
  { title: 'Australia', value: 'Australia' },
  { title: 'Austria', value: 'Austria' },
  { title: 'Azerbaijan', value: 'Azerbaijan' },
  { title: 'Bahamas', value: 'Bahamas' },
  { title: 'Bahrain', value: 'Bahrain' },
  { title: 'Bangladesh', value: 'Bangladesh' },
  { title: 'Barbados', value: 'Barbados' },
  { title: 'Belarus', value: 'Belarus' },
  { title: 'Belgium', value: 'Belgium' },
  { title: 'Belize', value: 'Belize' },
  { title: 'Benin', value: 'Benin' },
  { title: 'Bhutan', value: 'Bhutan' },
  { title: 'Bolivia', value: 'Bolivia' },
  { title: 'Bosnia and Herzegovina', value: 'Bosnia and Herzegovina' },
  { title: 'Botswana', value: 'Botswana' },
  { title: 'Brazil', value: 'Brazil' },
  { title: 'Brunei', value: 'Brunei' },
  { title: 'Bulgaria', value: 'Bulgaria' },
  { title: 'Burkina Faso', value: 'Burkina Faso' },
  { title: 'Burundi', value: 'Burundi' },
  { title: 'Cambodia', value: 'Cambodia' },
  { title: 'Cameroon', value: 'Cameroon' },
  { title: 'Canada', value: 'Canada' },
  { title: 'Cape Verde', value: 'Cape Verde' },
  { title: 'Central African Republic', value: 'Central African Republic' },
  { title: 'Chad', value: 'Chad' },
  { title: 'Chile', value: 'Chile' },
  { title: 'China', value: 'China' },
  { title: 'Colombia', value: 'Colombia' },
  { title: 'Comoros', value: 'Comoros' },
  { title: 'Congo', value: 'Congo' },
  { title: 'Costa Rica', value: 'Costa Rica' },
  { title: 'Croatia', value: 'Croatia' },
  { title: 'Cuba', value: 'Cuba' },
  { title: 'Cyprus', value: 'Cyprus' },
  { title: 'Czech Republic', value: 'Czech Republic' },
  { title: 'Democratic Republic of the Congo', value: 'Democratic Republic of the Congo' },
  { title: 'Denmark', value: 'Denmark' },
  { title: 'Djibouti', value: 'Djibouti' },
  { title: 'Dominica', value: 'Dominica' },
  { title: 'Dominican Republic', value: 'Dominican Republic' },
  { title: 'East Timor', value: 'East Timor' },
  { title: 'Ecuador', value: 'Ecuador' },
  { title: 'Egypt', value: 'Egypt' },
  { title: 'El Salvador', value: 'El Salvador' },
  { title: 'Equatorial Guinea', value: 'Equatorial Guinea' },
  { title: 'Eritrea', value: 'Eritrea' },
  { title: 'Estonia', value: 'Estonia' },
  { title: 'Ethiopia', value: 'Ethiopia' },
  { title: 'Fiji', value: 'Fiji' },
  { title: 'Finland', value: 'Finland' },
  { title: 'France', value: 'France' },
  { title: 'Gabon', value: 'Gabon' },
  { title: 'Gambia', value: 'Gambia' },
  { title: 'Georgia', value: 'Georgia' },
  { title: 'Germany', value: 'Germany' },
  { title: 'Ghana', value: 'Ghana' },
  { title: 'Greece', value: 'Greece' },
  { title: 'Grenada', value: 'Grenada' },
  { title: 'Guatemala', value: 'Guatemala' },
  { title: 'Guinea', value: 'Guinea' },
  { title: 'Guinea-Bissau', value: 'Guinea-Bissau' },
  { title: 'Guyana', value: 'Guyana' },
  { title: 'Haiti', value: 'Haiti' },
  { title: 'Honduras', value: 'Honduras' },
  { title: 'Hungary', value: 'Hungary' },
  { title: 'Iceland', value: 'Iceland' },
  { title: 'India', value: 'India' },
  { title: 'Indonesia', value: 'Indonesia' },
  { title: 'Iran', value: 'Iran' },
  { title: 'Iraq', value: 'Iraq' },
  { title: 'Ireland', value: 'Ireland' },
  { title: 'Israel', value: 'Israel' },
  { title: 'Italy', value: 'Italy' },
  { title: 'Ivory Coast', value: 'Ivory Coast' },
  { title: 'Jamaica', value: 'Jamaica' },
  { title: 'Japan', value: 'Japan' },
  { title: 'Jordan', value: 'Jordan' },
  { title: 'Kazakhstan', value: 'Kazakhstan' },
  { title: 'Kenya', value: 'Kenya' },
  { title: 'Kiribati', value: 'Kiribati' },
  { title: 'Kuwait', value: 'Kuwait' },
  { title: 'Kyrgyzstan', value: 'Kyrgyzstan' },
  { title: 'Laos', value: 'Laos' },
  { title: 'Latvia', value: 'Latvia' },
  { title: 'Lebanon', value: 'Lebanon' },
  { title: 'Lesotho', value: 'Lesotho' },
  { title: 'Liberia', value: 'Liberia' },
  { title: 'Libya', value: 'Libya' },
  { title: 'Liechtenstein', value: 'Liechtenstein' },
  { title: 'Lithuania', value: 'Lithuania' },
  { title: 'Luxembourg', value: 'Luxembourg' },
  { title: 'Madagascar', value: 'Madagascar' },
  { title: 'Malawi', value: 'Malawi' },
  { title: 'Malaysia', value: 'Malaysia' },
  { title: 'Maldives', value: 'Maldives' },
  { title: 'Mali', value: 'Mali' },
  { title: 'Malta', value: 'Malta' },
  { title: 'Marshall Islands', value: 'Marshall Islands' },
  { title: 'Mauritania', value: 'Mauritania' },
  { title: 'Mauritius', value: 'Mauritius' },
  { title: 'Mexico', value: 'Mexico' },
  { title: 'Micronesia', value: 'Micronesia' },
  { title: 'Moldova', value: 'Moldova' },
  { title: 'Monaco', value: 'Monaco' },
  { title: 'Mongolia', value: 'Mongolia' },
  { title: 'Montenegro', value: 'Montenegro' },
  { title: 'Morocco', value: 'Morocco' },
  { title: 'Mozambique', value: 'Mozambique' },
  { title: 'Myanmar', value: 'Myanmar' },
  { title: 'Namibia', value: 'Namibia' },
  { title: 'Nauru', value: 'Nauru' },
  { title: 'Nepal', value: 'Nepal' },
  { title: 'Netherlands', value: 'Netherlands' },
  { title: 'New Zealand', value: 'New Zealand' },
  { title: 'Nicaragua', value: 'Nicaragua' },
  { title: 'Niger', value: 'Niger' },
  { title: 'Nigeria', value: 'Nigeria' },
  { title: 'North Korea', value: 'North Korea' },
  { title: 'North Macedonia', value: 'North Macedonia' },
  { title: 'Norway', value: 'Norway' },
  { title: 'Oman', value: 'Oman' },
  { title: 'Pakistan', value: 'Pakistan' },
  { title: 'Palau', value: 'Palau' },
  { title: 'Panama', value: 'Panama' },
  { title: 'Papua New Guinea', value: 'Papua New Guinea' },
  { title: 'Paraguay', value: 'Paraguay' },
  { title: 'Peru', value: 'Peru' },
  { title: 'Philippines', value: 'Philippines' },
  { title: 'Poland', value: 'Poland' },
  { title: 'Portugal', value: 'Portugal' },
  { title: 'Qatar', value: 'Qatar' },
  { title: 'Romania', value: 'Romania' },
  { title: 'Russia', value: 'Russia' },
  { title: 'Rwanda', value: 'Rwanda' },
  { title: 'Saint Kitts and Nevis', value: 'Saint Kitts and Nevis' },
  { title: 'Saint Lucia', value: 'Saint Lucia' },
  { title: 'Saint Vincent and the Grenadines', value: 'Saint Vincent and the Grenadines' },
  { title: 'Samoa', value: 'Samoa' },
  { title: 'San Marino', value: 'San Marino' },
  { title: 'Sao Tome and Principe', value: 'Sao Tome and Principe' },
  { title: 'Saudi Arabia', value: 'Saudi Arabia' },
  { title: 'Senegal', value: 'Senegal' },
  { title: 'Serbia', value: 'Serbia' },
  { title: 'Seychelles', value: 'Seychelles' },
  { title: 'Sierra Leone', value: 'Sierra Leone' },
  { title: 'Singapore', value: 'Singapore' },
  { title: 'Slovakia', value: 'Slovakia' },
  { title: 'Slovenia', value: 'Slovenia' },
  { title: 'Solomon Islands', value: 'Solomon Islands' },
  { title: 'Somalia', value: 'Somalia' },
  { title: 'South Africa', value: 'South Africa' },
  { title: 'South Korea', value: 'South Korea' },
  { title: 'South Sudan', value: 'South Sudan' },
  { title: 'Spain', value: 'Spain' },
  { title: 'Sri Lanka', value: 'Sri Lanka' },
  { title: 'Sudan', value: 'Sudan' },
  { title: 'Suriname', value: 'Suriname' },
  { title: 'Sweden', value: 'Sweden' },
  { title: 'Switzerland', value: 'Switzerland' },
  { title: 'Syria', value: 'Syria' },
  { title: 'Taiwan', value: 'Taiwan' },
  { title: 'Tajikistan', value: 'Tajikistan' },
  { title: 'Tanzania', value: 'Tanzania' },
  { title: 'Thailand', value: 'Thailand' },
  { title: 'Togo', value: 'Togo' },
  { title: 'Tonga', value: 'Tonga' },
  { title: 'Trinidad and Tobago', value: 'Trinidad and Tobago' },
  { title: 'Tunisia', value: 'Tunisia' },
  { title: 'Turkey', value: 'Turkey' },
  { title: 'Turkmenistan', value: 'Turkmenistan' },
  { title: 'Tuvalu', value: 'Tuvalu' },
  { title: 'Uganda', value: 'Uganda' },
  { title: 'Ukraine', value: 'Ukraine' },
  { title: 'United Arab Emirates', value: 'United Arab Emirates' },
  { title: 'United Kingdom', value: 'United Kingdom' },
  { title: 'United States', value: 'United States' },
  { title: 'Uruguay', value: 'Uruguay' },
  { title: 'Uzbekistan', value: 'Uzbekistan' },
  { title: 'Vanuatu', value: 'Vanuatu' },
  { title: 'Vatican City', value: 'Vatican City' },
  { title: 'Venezuela', value: 'Venezuela' },
  { title: 'Vietnam', value: 'Vietnam' },
  { title: 'Yemen', value: 'Yemen' },
  { title: 'Zambia', value: 'Zambia' },
  { title: 'Zimbabwe', value: 'Zimbabwe' },
];
